import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import * as loginActions from 'core/duck';
import { withTranslation } from 'react-i18next';
import Auth from 'core/services/authToken';
import { ValidationService } from 'shared/services';
import { SuccessScreen, AlertNotice, ModalInfo } from 'shared/components';
import { ErrorTypes } from 'core/errorTypes';
import Parser from 'html-react-parser';
import { withRouterLegacy } from 'core/routerUtils';
import './Login.scss';

class Login extends Component {
  constructor(props) {
    super(props);

    this.state = {
      username: '',
      password: '',
      showPassword: false,
      rememberChecked: false,
      errorLogin: false,
      userNotConfirmed: false,
      termsModalOpen: false,
      isResend: false,
    };

    this.renderLogin = this.renderLogin.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onChange = this.onChange.bind(this);
    this.toggleShow = this.toggleShow.bind(this);
    this.toggleModal = this.toggleModal.bind(this);
    this.handleCheck = this.handleCheck.bind(this);
    this.validateInput = this.validateInput.bind(this);
    this.resendVerificationEmail = this.resendVerificationEmail.bind(this);
    this.onConfirmResend = this.onConfirmResend.bind(this);
  }

  componentDidMount() {
    if (Auth.authToken) {
      this.props.history.push('/dashboard');
    }
    this.props.actions.getAccountTerms();
  }

  componentDidUpdate(prevProps) {
    const { auth, error, verificationSent } = this.props;

    if (
      auth?.access_token &&
      !auth.Message &&
      (!prevProps.auth || prevProps.auth.access_token !== auth.access_token)
    ) {
      Auth.setAuthTokens(auth, this.state.rememberChecked);
      this.props.history.push('/dashboard');
      return;
    }

    if (error && error !== prevProps.error) {
      if (error?.Error === 'INVALID_TERMS_AND_CONDITIONS') {
        this.toggleModal();
        this.setState({
          invalidTermsError: 'errors.invalidTerms',
        });
        return;
      }

      let warningMessage;
      let userNotConfirmed;

      warningMessage = error.Error || error.Message;
      userNotConfirmed = Object.keys(ErrorTypes).indexOf(error.Error) > -1;
      if (userNotConfirmed) {
        warningMessage = this.props.t('errors.userNotConfirmed');
      }

      this.setState({
        warningMessage,
        userNotConfirmed,
        invalidTermsError: null,
      });
    } else if (prevProps.verificationSent !== verificationSent && !verificationSent) {
      this.setState({
        warningMessage: null,
        userNotConfirmed: false,
        invalidTermsError: null,
      });
    }
  }

  onSubmit(e, tncVersion) {
    e.preventDefault();
    if (this.validateInput()) {
      this.props.actions
        .loginUser({
          username: this.state.username,
          password: this.state.password,
          tncVersion,
        })
        .then(() => {
          sessionStorage.setItem('fontSize', 'default');
          sessionStorage.setItem('notificationTooltip', true);
        });
    }
  }

  onConfirmResend() {
    this.props.actions.resendVerificationAction({ data: false });
    this.setState({
      password: '',
      username: '',
      isResend: true,
    });
  }

  resendVerificationEmail(e) {
    e.preventDefault();
    this.props.actions.resendVerification({
      username: this.state.username,
      password: this.state.password,
    });
  }

  onChange(e) {
    if (this.state.isResend) {
      setTimeout(() => {
        this.setState({
          isResend: false,
        });
      }, 1000);
    } else {
      this.setState({
        [e.target.name]: e.target.value,
      });
    }
  }

  validateInput() {
    this.setState({
      errorLogin: false,
    });
    if (!this.state.username && !this.state.password) {
      this.setState({
        warningMessage: 'login.noEmailPassword',
      });
      return false;
    } else if (!this.state.username) {
      this.setState({
        warningMessage: 'login.noEmail',
      });
      return false;
    } else if (!ValidationService.validEmail(this.state.username)) {
      this.setState({
        warningMessage: 'errors.emailInvalid',
      });
    } else if (!this.state.password) {
      this.setState({
        warningMessage: 'login.noPassword',
      });
      return false;
    }

    this.setState({
      warningMessage: '',
    });
    return true;
  }

  toggleShow() {
    this.setState((prevState) => ({
      showPassword: !prevState.showPassword,
    }));
  }

  handleCheck(e) {
    this.setState({
      rememberChecked: e.target.checked,
    });
  }

  toggleModal() {
    this.setState((prevState) => ({
      termsModalOpen: !prevState.termsModalOpen,
    }));
  }

  renderLogin() {
    const { username, password, userNotConfirmed, warningMessage, invalidTermsError } = this.state;
    setTimeout(
      () =>
        Array.from(document.querySelectorAll('[data-bs-toggle="tooltip"]')).forEach((el) =>
          bootstrap.Tooltip.getOrCreateInstance(el).enable()
        ),
      500
    );
    return (
      <div>
        <form onSubmit={this.onSubmit}>
          <h4 className="card-heading">{this.props.t('login.header')}</h4>
          <div className="input-group w-100">
            <input
              name="username"
              id="username"
              type="email"
              className="w-100"
              value={username}
              aria-label={this.props.t('common.placeholder.email')}
              placeholder={this.props.t('common.placeholder.email')}
              onChange={this.onChange}
            />
          </div>

          <div className="input-group w-100 mb-0">
            <input
              name="password"
              id="password"
              className="form-control"
              type={this.state.showPassword ? 'text' : 'password'}
              value={password}
              aria-label={this.props.t('common.placeholder.password')}
              placeholder={this.props.t('common.placeholder.password')}
              onChange={this.onChange}
              autoComplete={this.state.rememberChecked ? 'on' : 'off'}
            />

            <button
              className="btn btn-toggle btn-primary input-group-btn"
              type="button"
              onClick={this.toggleShow}>
              {this.state.showPassword
                ? this.props.t('common.labels.hide')
                : this.props.t('common.labels.show')}
            </button>
          </div>
          <div className="text-start">
            <span className="text-error text-small">
              {warningMessage
                ? this.props.t(warningMessage)
                : this.state.errorLogin
                ? this.props.t('login.wrongMessage')
                : null}
            </span>
            {userNotConfirmed && !this.props.verificationSent && (
              <button
                className="btn btn-small w-100 mt-2 ml-0"
                onClick={this.resendVerificationEmail}>
                {this.props.t('login.resendVerificationEmail')}
              </button>
            )}
          </div>
          {invalidTermsError && (
            <AlertNotice message={this.props.t(invalidTermsError)} type="error" />
          )}
          <button
            id="LoginButton"
            className="btn btn-primary login-btn w-100"
            type="submit"
            disabled={this.props.isLoading}>
            {this.props.t('login.loginLabel')}
          </button>
        </form>
        <div className="mt-3">
          <Link to="/forgot-password">{this.props.t('login.forgotPassword')}</Link>
        </div>
        <div className="mt-2">
          {this.props.t('login.accountQuestion')}
          <Link className="register-link" to="/register">
            {this.props.t('common.labels.clickHere')}
          </Link>
        </div>

        <ModalInfo
          className="irregularity-modal"
          modalIsOpen={this.state.termsModalOpen}
          closeModal={this.toggleModal}
          onSubmit={(e) => this.onSubmit(e, this.props.tncVersion)}
          headerTitle="login.termsMessage"
          saveLabel="login.acceptTerms">
          <div className="terms-content">{Parser(this.props.terms)}</div>
        </ModalInfo>
      </div>
    );
  }

  render() {
    return (
      <section className="login-container text-center">
        {this.props.verificationSent ? (
          <SuccessScreen
            data={{
              copy: 'register.emailVerificationMessage',
              buttonLabel: 'common.labels.continueToSignIn',
              callback: this.onConfirmResend,
            }}
          />
        ) : (
          this.renderLogin()
        )}
      </section>
    );
  }
}

function mapStateToProps(state) {
  return {
    currentUser: state.users.currentUser,
    auth: state.users.auth,
    error: state.users.error,
    isLoading: state.users.isLoading,
    verificationSent: state.users.verificationSent,
    terms: state.users.terms,
    tncVersion: state.users.tncVersion,
  };
}

function mapDispatchToProps(dispatch) {
  return { actions: bindActionCreators(loginActions, dispatch) };
}

export default withTranslation()(
  withRouterLegacy(connect(mapStateToProps, mapDispatchToProps)(Login))
);

Login.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  actions: PropTypes.shape({
    loginUser: PropTypes.func.isRequired,
    resendVerificationAction: PropTypes.func.isRequired,
    resendVerification: PropTypes.func.isRequired,
    getAccountTerms: PropTypes.func.isRequired,
  }).isRequired,
  t: PropTypes.func.isRequired,
  error: PropTypes.shape({
    Error: PropTypes.string,
    Message: PropTypes.string,
  }),
  auth: PropTypes.shape({
    access_token: PropTypes.string,
    Message: PropTypes.string,
  }),
  terms: PropTypes.string,
  tncVersion: PropTypes.string,
  isLoading: PropTypes.bool,
  verificationSent: PropTypes.bool,
  currentUser: PropTypes.shape({}),
};
